// Hand-written query / mutation surface for /admin/mm/*. Same idiom
// as web-backoffice: a `keys` factory for invalidation, fetchers that
// throw APIError on failure, and TanStack Query consumers in pages.
import { APIError, api, qs } from "./api.ts";
import { fetchMeOrNull } from "./auth.ts";
import { accountScope } from "./brokerAccount.ts";
import type { CurrencyListResponse } from "./currency-types.ts";
import type {
  MMAccountBalanceHistory,
  MMAccountBalances,
  MMAccountDetail,
  MMAccountOrderActivity,
  MMAccountOrderFills,
  MMAccountOrderHistory,
  MMAccountPositions,
  MMAccountRuns,
  MMAccountTransferRequest,
  MMAccountTransferResponse,
  MMReturnLinkedAccountRequest,
  MMReturnLinkedAccountResponse,
  MMFundLinkedAccountRequest,
  MMFundLinkedAccountResponse,
  MMMoveReserveToParentRequest,
  SystemAccountTransferResponse,
  MyPermissions,
  MMBudgetSnapshot,
  MMFundingInfo,
  MMCreateMakerAccountRequest,
  MMUpdateMakerAccountFeesRequest,
  MMLinkedAccount,
  MMMakerAccount,
  MMMakerAccountsResponse,
  MMRepairReserveCurrenciesRequest,
  MMRepairReserveCurrenciesResponse,
  MMRunOrderFills,
  MMRunOrderActivity,
  MMRunPositions,
  MMRunBalances,
  MMRunStats,
  MMAutoStartResultsResponse,
  MMCapabilities,
  MMMarketSelection,
  MMMarketsResponse,
  MMPreset,
  MMPresetsResponse,
  MMRun,
  MMRunConfig,
  MMRunEventsResponse,
  MMRunsResponse,
  TradingMode,
  MMStrategiesResponse,
} from "./types.ts";

export const keys = {
  me: () => ["me"] as const,
  myPermissions: () => ["me", "permissions"] as const,
  // The operator currency registry (SM-485). MM Web reads it for
  // PRESENTATION only — names, marks, colours, display precision. Every
  // write lives in Backoffice.
  currencyRegistry: () => ["currency", "registry"] as const,
  strategies: () => ["mm", "strategies"] as const,
  // Keyed by mode: the picker asks the SERVER for the preset's own
  // mode, so a CASH page and a GAME page are different queries, not one
  // cached list filtered twice (SM-525).
  instruments: (mode?: TradingMode) => ["mm", "instruments", mode ?? ""] as const,
  accounts: (q: string | undefined, limit: number, offset: number) =>
    ["mm", "accounts", q ?? "", limit, offset] as const,
  presets: (limit: number, offset: number, mode?: TradingMode) =>
    ["mm", "presets", limit, offset, mode ?? ""] as const,
  preset: (id: string) => ["mm", "preset", id] as const,
  presetDefault: () => ["mm", "preset-default"] as const,
  runs: (status: string | undefined, limit: number, offset: number, mode?: TradingMode) =>
    ["mm", "runs", status ?? "", limit, offset, mode ?? ""] as const,
  run: (id: string) => ["mm", "run", id] as const,
  runEvents: (id: string, kind: string | undefined, limit: number, offset: number) =>
    ["mm", "run-events", id, kind ?? "", limit, offset] as const,
  runBudget: (id: string) => ["mm", "run-budget", id] as const,
  runStats: (id: string, top: number) => ["mm", "run-stats", id, top] as const,
  runOrderActivity: (id: string, limit: number, offset: number) => ["mm", "run-order-activity", id, limit, offset] as const,
  runOrderFills: (id: string, limit: number, offset: number) => ["mm", "run-order-fills", id, limit, offset] as const,
  runPositions: (id: string, limit: number, offset: number) => ["mm", "run-positions", id, limit, offset] as const,
  runBalances: (id: string, limit: number, offset: number) => ["mm", "run-balances", id, limit, offset] as const,
  runAttempts: (id: string) => ["mm", "run-attempts", id] as const,
  // The preset run-history read is the SAME paginated /runs endpoint,
  // keyed by its filter set so a tab switch or a filter change is its
  // own cache entry rather than clobbering the unfiltered list.
  presetRuns: (filter: RunHistoryFilter) => ["mm", "preset-runs", filter] as const,
  autoStartResults: () => ["mm", "auto-start-results"] as const,
  capabilities: () => ["mm", "capabilities"] as const,
  // `input` is whatever uniquely identifies the previewed config — the
  // preset editor passes a serialized pool snapshot so the key only
  // churns when funding-relevant fields change.
  fundingPreview: (input: unknown) => ["mm", "funding-preview", input] as const,
  // Per-currency balances for one linked desk (SM-470). Keyed by the
  // SYSTEM_MM child code, which is what the endpoint takes.
  linkedAccount: (code: string) => ["mm", "linked-account", code] as const,

  // SM-564: one account's detail, one key family per SECTION so a tab
  // or a currency change refetches exactly the section it touches, and
  // a mutation can invalidate balances without disturbing orders.
  accountDetail: (userID: string) => ["mm", "account", userID] as const,
  // SM-786: every section below is also scoped by the broker account
  // its figures were read for, when one is known. `accountScope` puts
  // it IN the user slot, so a key is the same length (keepWithinScope's
  // scope lengths hold) and — with no broker account — byte-identical
  // to the pre-SM-786 key.
  accountBalances: (userID: string, currency: string, brokerAccountID?: string) =>
    ["mm", "account-balances", accountScope(userID, brokerAccountID), currency] as const,
  accountOrders: (userID: string, filter: AccountSectionFilter, limit: number, offset: number, brokerAccountID?: string) =>
    ["mm", "account-orders", accountScope(userID, brokerAccountID), filter.currency ?? "", filter.trading_mode ?? "", limit, offset] as const,
  accountOrderHistory: (userID: string, filter: AccountSectionFilter, limit: number, offset: number, brokerAccountID?: string) =>
    ["mm", "account-order-history", accountScope(userID, brokerAccountID), filter.currency ?? "", filter.trading_mode ?? "", limit, offset] as const,
  accountFills: (userID: string, filter: AccountSectionFilter, limit: number, offset: number, brokerAccountID?: string) =>
    ["mm", "account-fills", accountScope(userID, brokerAccountID), filter.currency ?? "", filter.trading_mode ?? "", limit, offset] as const,
  accountPositions: (userID: string, filter: AccountSectionFilter, limit: number, offset: number, brokerAccountID?: string) =>
    ["mm", "account-positions", accountScope(userID, brokerAccountID), filter.currency ?? "", filter.trading_mode ?? "", limit, offset] as const,
  accountBalanceHistory: (userID: string, currency: string, limit: number, offset: number, brokerAccountID?: string) =>
    ["mm", "account-balance-history", accountScope(userID, brokerAccountID), currency, limit, offset] as const,
  accountRuns: (userID: string, filter: AccountRunsFilter, limit: number, offset: number, brokerAccountID?: string) =>
    ["mm", "account-runs", accountScope(userID, brokerAccountID), filter.statuses ?? "", filter.trading_mode ?? "", limit, offset] as const,
};

/** Narrows a trading section by currency (a canonical code) and/or mode. */
export interface AccountSectionFilter {
  currency?: string;
  trading_mode?: string;
}

export interface AccountRunsFilter {
  /** Comma-separated run statuses, as the /runs list takes them. */
  statuses?: string;
  trading_mode?: string;
}

// Prefixes include every filter, mode, page and entity in a query family.
export const keyPrefixes = {
  accounts: ["mm", "accounts"] as const,
  linkedAccounts: ["mm", "linked-account"] as const,
  strategies: ["mm", "strategies"] as const,
  instruments: ["mm", "instruments"] as const,
  presets: ["mm", "presets"] as const,
  presetDetail: ["mm", "preset"] as const,
  autoStartResults: ["mm", "auto-start-results"] as const,
  capabilities: ["mm", "capabilities"] as const,
  runs: ["mm", "runs"] as const,
  runHistory: ["mm", "preset-runs"] as const,
  runDetail: ["mm", "run"] as const,
  runAttempts: ["mm", "run-attempts"] as const,
  runBudget: ["mm", "run-budget"] as const,
  runStats: ["mm", "run-stats"] as const,
  runEvents: ["mm", "run-events"] as const,
  runOrderActivity: ["mm", "run-order-activity"] as const,
  runOrderFills: ["mm", "run-order-fills"] as const,
  runPositions: ["mm", "run-positions"] as const,
  runBalances: ["mm", "run-balances"] as const,
  accountDetail: ["mm", "account"] as const,
  accountBalances: ["mm", "account-balances"] as const,
  accountOrders: ["mm", "account-orders"] as const,
  accountOrderHistory: ["mm", "account-order-history"] as const,
  accountFills: ["mm", "account-fills"] as const,
  accountPositions: ["mm", "account-positions"] as const,
  accountBalanceHistory: ["mm", "account-balance-history"] as const,
  accountRuns: ["mm", "account-runs"] as const,
};

// Stable name for the auth fetcher so route loaders import it from
// the queries module (matches the rest of the platform's idiom).
export const fetchMe = fetchMeOrNull;

export function fetchMyPermissions() {
  return api<MyPermissions>("/admin/me/permissions");
}

/**
 * The operator currency registry, read for presentation.
 *
 * Same endpoint Backoffice's settings page reads — which is what makes the two
 * consoles draw one currency the same way rather than promising to. Requires
 * CURRENCY_VIEW; a caller without it gets a 403 and falls back to the contract
 * seed, which is a complete presentation on its own.
 */
export function fetchCurrencyRegistry() {
  return api<CurrencyListResponse>("/admin/currencies").then((r) => r.currencies ?? []);
}

// --- Catalog (read-only) ---

export function fetchStrategies() {
  return api<MMStrategiesResponse>("/admin/mm/strategies");
}

/**
 * The OPEN market catalog, each row tagged with the modes it may be
 * quoted in.
 *
 * `mode` narrows SERVER-SIDE. Filtering in the browser instead would
 * mean shipping GAME-ineligible markets to a GAME picker and trusting
 * every render path to hide them — one missed branch and an operator
 * selects an instrument the run will silently skip.
 */
export function fetchInstruments(mode?: TradingMode) {
  return api<MMMarketsResponse>(`/admin/mm/markets${qs({ trading_mode: mode })}`);
}

// --- Maker accounts ---

/**
 * The per-currency view of one desk's execution wallet family.
 *
 * `GET /admin/mm/accounts` carries only the legacy GOLD-shaped fields,
 * so the RED and GREEN triples have to come from here — one read per
 * linked desk. It is the same endpoint Backoffice Treasury uses for the
 * linked-account panel, which is why the two consoles cannot disagree
 * about what a desk holds.
 */
export function fetchLinkedAccount(fundingAccountCode: string) {
  return api<MMLinkedAccount>(
    `/admin/system-accounts/${encodeURIComponent(fundingAccountCode)}/linked-mm`,
  );
}

export function fetchMakerAccounts(q?: string, limit = 50, offset = 0) {
  return api<MMMakerAccountsResponse>(`/admin/mm/accounts${qs({ q, limit, offset })}`);
}

// --- SM-564: one account's detail -----------------------------------
//
// Every fetcher THROWS on failure (no null-wrapping): each section
// owns its own error surface, and a swallowed error would render as an
// empty desk. An unknown account is a 404 (MM_000226), which the page
// shows as not-found rather than as a blank header.

export function fetchAccountDetail(userID: string) {
  return api<MMAccountDetail>(`/admin/mm/accounts/${encodeURIComponent(userID)}`);
}

export function fetchAccountBalances(userID: string, currency = "") {
  return api<MMAccountBalances>(
    `/admin/mm/accounts/${encodeURIComponent(userID)}/balances${qs({ currency })}`,
  );
}

export function fetchAccountOrders(userID: string, filter: AccountSectionFilter, limit = 25, offset = 0) {
  return api<MMAccountOrderActivity>(
    `/admin/mm/accounts/${encodeURIComponent(userID)}/orders${qs({ ...filter, limit, offset })}`,
  );
}

export function fetchAccountOrderHistory(userID: string, filter: AccountSectionFilter, limit = 25, offset = 0) {
  return api<MMAccountOrderHistory>(
    `/admin/mm/accounts/${encodeURIComponent(userID)}/order-history${qs({ ...filter, limit, offset })}`,
  );
}

export function fetchAccountFills(userID: string, filter: AccountSectionFilter, limit = 25, offset = 0) {
  return api<MMAccountOrderFills>(
    `/admin/mm/accounts/${encodeURIComponent(userID)}/fills${qs({ ...filter, limit, offset })}`,
  );
}

export function fetchAccountPositions(userID: string, filter: AccountSectionFilter, limit = 25, offset = 0) {
  return api<MMAccountPositions>(
    `/admin/mm/accounts/${encodeURIComponent(userID)}/positions${qs({ ...filter, limit, offset })}`,
  );
}

/** Balance history pages ONE wallet, so the currency is required. */
export function fetchAccountBalanceHistory(userID: string, currency: string, limit = 25, offset = 0) {
  return api<MMAccountBalanceHistory>(
    `/admin/mm/accounts/${encodeURIComponent(userID)}/balance-history${qs({ currency, limit, offset })}`,
  );
}

export function fetchAccountRuns(userID: string, filter: AccountRunsFilter, limit = 25, offset = 0) {
  return api<MMAccountRuns>(
    `/admin/mm/accounts/${encodeURIComponent(userID)}/runs${qs({ ...filter, limit, offset })}`,
  );
}

/**
 * Provision the currency wallets a linked reserve is missing (SM-526).
 *
 * NOT wrapped to resolve null: an operator who pressed Repair must be
 * told whether it worked. It moves no money — a provisioned wallet
 * starts at zero — and it is idempotent, so a retry after a lost
 * response is safe.
 */
export function repairReserveCurrencies(body: MMRepairReserveCurrenciesRequest = {}) {
  return api<MMRepairReserveCurrenciesResponse>("/admin/mm/accounts/reserve-currencies/repair", {
    method: "POST",
    body,
  });
}

export function createMakerAccount(body: MMCreateMakerAccountRequest) {
  return api<MMMakerAccount>("/admin/mm/accounts", { method: "POST", body });
}

export function updateMakerAccountFees(userID: string, body: MMUpdateMakerAccountFeesRequest) {
  return api<MMMakerAccount>(`/admin/mm/accounts/${encodeURIComponent(userID)}/fees`, { method: "PUT", body });
}

// transferBetweenAccounts — move execution balance between two MM
// accounts (SM-380).
//
// Deliberately NOT wrapped in a try/catch that resolves to null the way
// the optional read fetchers are: a failed money movement must reach
// the caller as an error so the operator is told, rather than silently
// rendering nothing and leaving them to guess whether it went through.
export function transferBetweenAccounts(body: MMAccountTransferRequest) {
  return api<MMAccountTransferResponse>("/admin/mm/accounts/transfers", { method: "POST", body });
}

// The path names the linked reserve. The server resolves its MM user
// from the persisted funding link, so the browser cannot redirect the
// debit or choose a different SYSTEM_MM destination.
export function returnExecutionToReserve(
  fundingAccountCode: string,
  body: MMReturnLinkedAccountRequest,
) {
  return api<MMReturnLinkedAccountResponse>(
    `/admin/system-accounts/${encodeURIComponent(fundingAccountCode)}/linked-mm/return`,
    { method: "POST", body },
  );
}

export function fundExecutionFromReserve(
  fundingAccountCode: string,
  body: MMFundLinkedAccountRequest,
) {
  return api<MMFundLinkedAccountResponse>(
    `/admin/system-accounts/${encodeURIComponent(fundingAccountCode)}/linked-mm/fund`,
    { method: "POST", body },
  );
}

/**
 * Sweep a linked MM child reserve into its fixed parent. This UI never
 * accepts a destination; SYSTEM_MM is a constant, not operator input.
 */
export function moveReserveToParent(
  fundingAccountCode: string,
  body: MMMoveReserveToParentRequest,
) {
  return api<SystemAccountTransferResponse>("/admin/system-accounts/transfer", {
    method: "POST",
    body: {
      operation_id: body.idempotency_key,
      source: { account_code: fundingAccountCode },
      destination: { account_code: "SYSTEM_MM" },
      amount: String(body.amount_minor),
      reason: body.reason,
      // Explicit currency (SM-568): a RED reserve sweeps to the parent's
      // RED wallet; the ledger's absent-means-GOLD fold must not decide.
      currency: body.currency,
    },
  });
}

// --- Presets ---

export function fetchPresets(limit: number, offset: number, mode?: TradingMode) {
  return api<MMPresetsResponse>(`/admin/mm/presets${qs({ limit, offset, trading_mode: mode })}`);
}

export function fetchPreset(id: string) {
  return api<MMPreset>(`/admin/mm/presets/${encodeURIComponent(id)}`);
}

export function fetchDefaultPreset() {
  return api<MMPreset>("/admin/mm/presets/default");
}

export function createPreset(body: {
  name: string;
  description: string;
  config: MMRunConfig;
  auto_start_enabled?: boolean;
}) {
  return api<MMPreset>("/admin/mm/presets", { method: "POST", body });
}

// auto_start_enabled is OPTIONAL here on purpose: omitting it leaves
// the flag alone. Sending `false` from an edit form that does not
// expose the toggle would disarm a preset the operator meant to keep.
export function updatePreset(id: string, body: {
  name: string;
  description: string;
  config: MMRunConfig;
  auto_start_enabled?: boolean;
}) {
  return api<MMPreset>(`/admin/mm/presets/${encodeURIComponent(id)}`, { method: "PUT", body });
}

export function deletePreset(id: string) {
  return api<{ deleted: string }>(`/admin/mm/presets/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export function duplicatePreset(id: string) {
  return api<MMPreset>(`/admin/mm/presets/${encodeURIComponent(id)}/duplicate`, { method: "POST" });
}

/**
 * @deprecated The exclusive single-default surface: it arms this
 * preset and disarms every other one. Use setPresetAutoStart, which
 * touches only the named preset.
 */
export function setDefaultPreset(id: string) {
  return api<MMPreset>(`/admin/mm/presets/${encodeURIComponent(id)}/set-default`, { method: "POST" });
}

/** Arm or disarm post-restart start for ONE preset. */
export function setPresetAutoStart(id: string, enabled: boolean) {
  return api<MMPreset>(`/admin/mm/presets/${encodeURIComponent(id)}/auto-start`, {
    method: "POST",
    body: { enabled },
  });
}

/**
 * The last boot's per-preset outcomes.
 *
 * Resolves to an empty report on any failure: a server that has not
 * auto-started presets is a configuration, not a fault, and the
 * Presets page must not break because this read 404s on an older
 * build.
 */
/**
 * The runtime's effective capabilities (SM-531 / SM-506). Null on an
 * older server that has no such endpoint — the Overview then says so
 * rather than guessing.
 */
export async function fetchCapabilities(): Promise<MMCapabilities | null> {
  try {
    return await api<MMCapabilities>("/admin/mm/capabilities");
  } catch {
    return null;
  }
}

export async function fetchAutoStartResults(): Promise<MMAutoStartResultsResponse> {
  try {
    return await api<MMAutoStartResultsResponse>("/admin/mm/presets-auto-start-results");
  } catch {
    return { results: [] };
  }
}

export function updatePresetInstruments(id: string, markets: MMMarketSelection) {
  return api<MMPreset>(`/admin/mm/presets/${encodeURIComponent(id)}/markets`, {
    method: "PATCH",
    body: { markets },
  });
}

// --- Runs ---

export function fetchRuns(
  status: string | undefined,
  limit: number,
  offset: number,
  mode?: TradingMode,
) {
  return api<MMRunsResponse>(`/admin/mm/runs${qs({ status, limit, offset, trading_mode: mode })}`);
}

/**
 * Facets the run list / preset run-history share.
 *
 * `statuses` is a SET rather than a single value so "all active" is
 * one request instead of six, and a paused run does not fall out of
 * an operator's "what's live" view.
 */
export interface RunHistoryFilter {
  preset_id?: string;
  status?: string;
  statuses?: string;
  trading_mode?: string;
  root_run_id?: string;
  /** Full or partial run id, matched case-insensitively. */
  run_id?: string;
  from?: string;
  to?: string;
  limit: number;
  offset: number;
}

/**
 * The preset run-history read.
 *
 * Deliberately the same endpoint as the Runs list: one paginated
 * source means filtering, paging and ordering cannot diverge between
 * "all runs" and "this preset's runs".
 */
export function fetchRunHistory(filter: RunHistoryFilter) {
  return api<MMRunsResponse>(`/admin/mm/runs${qs({ ...filter })}`);
}

/** Every attempt in the addressed run's recovery chain, oldest first. */
export function fetchRunAttempts(id: string) {
  return api<MMRunsResponse>(`/admin/mm/runs/${encodeURIComponent(id)}/attempts`);
}

export function fetchRun(id: string) {
  return api<MMRun>(`/admin/mm/runs/${encodeURIComponent(id)}`);
}

export interface StartRunBody {
  preset_id?: string;
  config?: MMRunConfig;
  /**
   * The confirmed funding preview's `plan_fingerprint` (SM-526). The
   * server refuses (409 MM_000224) if the preset's plan changed since,
   * so the rows the operator confirmed are the plan that executes.
   */
  expected_funding_plan?: string;
}

export function startRun(body: StartRunBody) {
  return api<MMRun>("/admin/mm/runs", { method: "POST", body });
}

export function stopRun(id: string) {
  return api<MMRun>(`/admin/mm/runs/${encodeURIComponent(id)}/stop`, { method: "POST" });
}

/** Gate the run's execution and drain its resting orders. */
export function pauseRun(id: string, reason?: string) {
  return api<MMRun>(`/admin/mm/runs/${encodeURIComponent(id)}/pause`, {
    method: "POST",
    body: { reason: reason ?? "" },
  });
}

/** Continue the SAME run from its checkpoint. */
export function resumeRun(id: string, reason?: string) {
  return api<MMRun>(`/admin/mm/runs/${encodeURIComponent(id)}/resume`, {
    method: "POST",
    body: { reason: reason ?? "" },
  });
}

/**
 * Recover a FAILED run as a NEW linked attempt.
 *
 * The response is a DIFFERENT run than the id in the path: the failed
 * run is never reopened, so callers must navigate to the returned
 * run rather than refreshing the one they were looking at.
 *
 * `mode` is explicit and never silently degrades — asking for
 * "checkpoint" with no compatible checkpoint is a coded refusal, not
 * a quiet fresh start.
 */
export function restartRun(id: string, mode: "checkpoint" | "fresh", reason?: string) {
  return api<MMRun>(`/admin/mm/runs/${encodeURIComponent(id)}/restart`, {
    method: "POST",
    body: { mode, reason: reason ?? "" },
  });
}

/**
 * Run-scoped open positions.
 *
 * Never resolves to null on failure the way the optional stats read
 * does: a section that could not be read reports UNAVAILABLE in its
 * own state header, and swallowing the error here would strip that
 * distinction before the UI could show it.
 */
export function fetchRunPositions(id: string, limit = 50, offset = 0) {
  return api<MMRunPositions>(
    `/admin/mm/runs/${encodeURIComponent(id)}/positions${qs({ limit, offset })}`,
  );
}

/** Run-scoped per-(account, currency) execution balances. */
export function fetchRunBalances(id: string, limit = 50, offset = 0) {
  return api<MMRunBalances>(
    `/admin/mm/runs/${encodeURIComponent(id)}/balances${qs({ limit, offset })}`,
  );
}

// Body of POST /admin/mm/funding/preview — same resolution semantics
// as startRun: preset_id alone uses the saved preset, config alone
// previews an ad-hoc run, both together preview the preset with the
// config patched on top.
export interface FundingPreviewBody {
  preset_id?: string;
  config?: MMRunConfig;
}

// fetchFundingPreview — SM-152 pre-start funding requirement summary.
// Resolves to null on ANY failure (older servers 404 the route, a
// server without the funding service 501s, a ledger-down preview 502s)
// so callers render nothing / skip the pre-flight instead of breaking
// the page — same idiom as fetchRunStats.
export async function fetchFundingPreview(body: FundingPreviewBody): Promise<MMFundingInfo | null> {
  try {
    return await api<MMFundingInfo>("/admin/mm/funding/preview", { method: "POST", body });
  } catch {
    return null;
  }
}

export function fetchRunEvents(id: string, kind: string | undefined, limit: number, offset: number) {
  return api<MMRunEventsResponse>(
    `/admin/mm/runs/${encodeURIComponent(id)}/events${qs({ kind, limit, offset })}`,
  );
}

export function fetchRunBudget(id: string) {
  return api<MMBudgetSnapshot>(`/admin/mm/runs/${encodeURIComponent(id)}/budget`);
}

// fetchRunStats — senior-quant telemetry for one active run.
// Resolves to null when the run is terminal (server returns 4xx with
// MMRunNotRunning); the caller renders the "no live stats" state.
// `top` caps the per-market and per-maker tables (default 10).
export async function fetchRunStats(id: string, top = 10): Promise<MMRunStats | null> {
  try {
    return await api<MMRunStats>(
      `/admin/mm/runs/${encodeURIComponent(id)}/stats?top=${top}`,
    );
  } catch (e) {
    // The endpoint 4xxs when the run is terminal — surface as null so
    // the UI can render the "no live stats" state without try/catch
    // at the call site.
    return null;
  }
}

export async function fetchRunOrderActivity(id: string, limit = 50, offset = 0): Promise<MMRunOrderActivity | null> {
  try {
    return await api<MMRunOrderActivity>(
      `/admin/mm/runs/${encodeURIComponent(id)}/order-activity${qs({ limit, offset })}`,
    );
  } catch (e) {
    if (e instanceof APIError && e.code === "MM_000212") return null;
    throw e;
  }
}

export async function fetchRunOrderFills(id: string, limit = 50, offset = 0): Promise<MMRunOrderFills | null> {
  try {
    return await api<MMRunOrderFills>(
      `/admin/mm/runs/${encodeURIComponent(id)}/order-fills${qs({ limit, offset })}`,
    );
  } catch (e) {
    if (e instanceof APIError && e.code === "MM_000212") return null;
    throw e;
  }
}
